import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, Modal,
  TextInput, Dimensions, Animated, Platform, StatusBar,
  BackHandler, Switch, Alert, Share,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── AI SERVICE ───────────────────────────────────────────────────────────────
let aiService = null;
try { aiService = require('../../../services/aiService'); } catch {}


const { width: W } = Dimensions.get('window');

const BRAND = {
  blue: '#0e4e96', canary: '#fdb913', mint: '#19abb5',
  bg: '#f8fafc', surface: '#ffffff', text: '#0f172a', sub: '#475569',
  line: '#e2e8f0', success: '#10b981', warning: '#f59e0b',
  danger: '#ef4444', info: '#3b82f6', purple: '#8b5cf6', pink: '#ec4899',
};

const APP_VERSION = '2.0.0';
const BUILD = '20250125';

const INTEGRATIONS = [
  { id: 'crm_hub', name: 'CRM Hub', desc: 'Full pipeline & deals integration', icon: 'handshake', color: '#f97316', connected: true, lastSync: '2 min ago' },
  { id: 'scaleup', name: 'Scale-Up Platform', desc: 'Business growth tools', icon: 'rocket', color: BRAND.purple, connected: true, lastSync: '5 min ago' },
  { id: 'ml_platform', name: 'ML Platform', desc: 'AI & predictive analytics', icon: 'brain', color: '#667eea', connected: true, lastSync: '10 min ago' },
  { id: 'xero', name: 'Xero Accounting', desc: 'Accounting & bookkeeping', icon: 'bank', color: '#13B5EA', connected: false, lastSync: null },
  { id: 'slack', name: 'Slack', desc: 'Team notifications', icon: 'slack', color: '#4A154B', connected: false, lastSync: null },
  { id: 'google_workspace', name: 'Google Workspace', desc: 'Calendar, Drive, Gmail', icon: 'google', color: '#EA4335', connected: false, lastSync: null },
  { id: 'ms365', name: 'Microsoft 365', desc: 'Teams, Outlook, SharePoint', icon: 'microsoft', color: '#0078d4', connected: false, lastSync: null },
  { id: 'stripe', name: 'Stripe', desc: 'Payments & billing', icon: 'credit-card', color: '#6772e5', connected: false, lastSync: null },
  { id: 'hmrc', name: 'HMRC Making Tax Digital', desc: 'VAT & payroll submission', icon: 'bank-outline', color: '#005b99', connected: true, lastSync: '1 day ago' },
  { id: 'linkedin', name: 'LinkedIn', desc: 'HR & recruitment', icon: 'linkedin', color: '#0A66C2', connected: false, lastSync: null },
];

const NOTIFICATION_SETTINGS = [
  { key: 'dealAlerts', label: 'Deal Stage Changes', sub: 'CRM pipeline updates', icon: 'briefcase', color: '#f97316', value: true },
  { key: 'invoiceAlerts', label: 'Invoice Reminders', sub: 'Due & overdue invoices', icon: 'file-document', color: BRAND.success, value: true },
  { key: 'lowStock', label: 'Low Stock Alerts', sub: 'Inventory below reorder level', icon: 'alert', color: BRAND.danger, value: true },
  { key: 'payrollAlerts', label: 'Payroll Notifications', sub: 'Run approvals and completions', icon: 'currency-gbp', color: BRAND.pink, value: true },
  { key: 'projectDeadlines', label: 'Project Deadlines', sub: '48h and 24h reminders', icon: 'clock-alert', color: '#6366f1', value: true },
  { key: 'systemAlerts', label: 'System Alerts', sub: 'Sync errors, maintenance', icon: 'cog-outline', color: BRAND.sub, value: false },
  { key: 'reportReady', label: 'Report Generation', sub: 'Notify when reports are ready', icon: 'file-chart', color: BRAND.blue, value: true },
  { key: 'weeklyDigest', label: 'Weekly Business Digest', sub: 'Monday 8AM summary email', icon: 'email', color: BRAND.mint, value: true },
];

const SECURITY_LOG = [
  { action: 'Login', device: 'iPhone 15 Pro', location: 'London, UK', time: '2h ago', success: true },
  { action: 'Settings changed', device: 'MacBook Pro', location: 'London, UK', time: '3h ago', success: true },
  { action: 'Export – P&L Report', device: 'iPhone 15 Pro', location: 'London, UK', time: '5h ago', success: true },
  { action: 'Failed Login', device: 'Unknown', location: 'Unknown', time: '12h ago', success: false },
  { action: 'Password changed', device: 'MacBook Pro', location: 'London, UK', time: '3 days ago', success: true },
];

const ERPSettingsScreen = ({ navigation }) => {
  const [settings, setSettings] = useState({
    darkMode: false,
    biometric: true,
    twoFactor: true,
    autoSync: true,
    syncInterval: '15',
    cacheEnabled: true,
    offlineMode: false,
    dataRetention: '90',
    currency: 'GBP',
    dateFormat: 'DD/MM/YYYY',
    timezone: 'Europe/London',
    language: 'en-GB',
    compactView: false,
    showTutorials: true,
    betaFeatures: false,
  });
  const [notifications, setNotifications] = useState(NOTIFICATION_SETTINGS);
  const [integrations, setIntegrations] = useState(INTEGRATIONS);
  const [activeTab, setActiveTab] = useState('general');
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showIntegrationDetail, setShowIntegrationDetail] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showDataModal, setShowDataModal] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: 'Salah Ibrahim', email: 'info@jobeng.in', phone: '+44 7700 900000', role: 'Administrator', company: 'JobEng Ltd', department: 'Management' });
  const [saving, setSaving] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fadeAnim, { toValue: 1, duration: 500, useNativeDriver: true }).start();
    loadSettings();
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (showProfileEdit || showIntegrationDetail || showDataModal) { setShowProfileEdit(false); setShowIntegrationDetail(false); setShowDataModal(false); return true; }
      return false;
    });
    return () => sub.remove();
  }, [showProfileEdit, showIntegrationDetail, showDataModal]);

  const loadSettings = async () => {
    try {
      const stored = await AsyncStorage.getItem('erp_settings_v2');
      if (stored) setSettings(prev => ({ ...prev, ...JSON.parse(stored) }));
    } catch {}
  };

  const saveSetting = async (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    try { await AsyncStorage.setItem('erp_settings_v2', JSON.stringify(updated)); } catch {}
  };

  const toggleNotification = (key) => {
    setNotifications(prev => prev.map(n => n.key === key ? { ...n, value: !n.value } : n));
  };

  const toggleIntegration = (id) => {
    const intg = integrations.find(i => i.id === id);
    if (intg?.connected) {
      Alert.alert('Disconnect', `Disconnect ${intg.name}?`, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Disconnect', style: 'destructive', onPress: () => setIntegrations(prev => prev.map(i => i.id === id ? { ...i, connected: false, lastSync: null } : i)) },
      ]);
    } else {
      Alert.alert('Connect', `Connect to ${intg?.name}? You'll need to authenticate.`, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Connect', onPress: () => setIntegrations(prev => prev.map(i => i.id === id ? { ...i, connected: true, lastSync: 'Just now' } : i)) },
      ]);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 800));
    setSaving(false);
    setShowProfileEdit(false);
    Alert.alert('✅ Saved', 'Profile updated successfully.');
  };

  const handleClearCache = () => {
    Alert.alert('Clear Cache', 'This will remove all locally cached ERP data. The app will re-fetch fresh data on next launch.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Clear', style: 'destructive', onPress: async () => { const keys = await AsyncStorage.getAllKeys(); const erpKeys = keys.filter(k => k.startsWith('erp_')); await AsyncStorage.multiRemove(erpKeys); Alert.alert('✅ Done', `${erpKeys.length} cache entries cleared.`); } },
    ]);
  };

  const handleExportData = async () => {
    await Share.share({
      message: `JobEng ERP — Data Export\n${'═'.repeat(30)}\nUser: ${profileForm.name}\nRole: ${profileForm.role}\nCompany: ${profileForm.company}\nExported: ${new Date().toLocaleString()}\nApp Version: ${APP_VERSION}\n\nSettings:\n${Object.entries(settings).map(([k, v]) => `${k}: ${v}`).join('\n')}`,
      title: 'ERP Data Export',
    });
  };

  const TABS = [
    { id: 'general', label: 'General', icon: 'cog' },
    { id: 'profile', label: 'Profile', icon: 'account' },
    { id: 'notifications', label: 'Alerts', icon: 'bell' },
    { id: 'integrations', label: 'Integrations', icon: 'link' },
    { id: 'security', label: 'Security', icon: 'shield' },
    { id: 'data', label: 'Data', icon: 'database' },
    { id: 'about', label: 'About', icon: 'information' },
  ];

  const SettingsGroup = ({ title, children }) => (
    <View style={{ marginBottom: 20 }}>
      <Text style={[sts.groupLabel, { color: BRAND.sub }]}>{title}</Text>
      <View style={[sts.groupCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>{children}</View>
    </View>
  );

  const SettingsRow = ({ icon, iconBg, label, sub, onPress, right, last }) => (
    <TouchableOpacity style={[sts.settingsRow, !last && { borderBottomWidth: 1, borderBottomColor: BRAND.line }]} onPress={onPress} activeOpacity={onPress ? 0.7 : 1}>
      <View style={[sts.settingsIcon, { backgroundColor: iconBg }]}><MaterialCommunityIcons name={icon} size={16} color={onPress ? BRAND.text : BRAND.sub} /></View>
      <View style={{ flex: 1 }}>
        <Text style={[sts.settingsLabel, { color: BRAND.text }]}>{label}</Text>
        {sub && <Text style={[sts.settingsSub, { color: BRAND.sub }]}>{sub}</Text>}
      </View>
      {right || (onPress && <MaterialCommunityIcons name="chevron-right" size={16} color={BRAND.line} />)}
    </TouchableOpacity>
  );

  const renderGeneral = () => (
    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 16, paddingBottom: 80 }}>
      <SettingsGroup title="APPEARANCE">
        <SettingsRow icon="theme-light-dark" iconBg={BRAND.purple + '15'} label="Dark Mode" sub="Switch to dark theme" right={<Switch value={settings.darkMode} onValueChange={v => saveSetting('darkMode', v)} trackColor={{ false: BRAND.line, true: BRAND.purple + '60' }} thumbColor={settings.darkMode ? BRAND.purple : BRAND.sub} />} />
        <SettingsRow icon="view-compact" iconBg={BRAND.blue + '15'} label="Compact View" sub="Reduce card padding and spacing" right={<Switch value={settings.compactView} onValueChange={v => saveSetting('compactView', v)} trackColor={{ false: BRAND.line, true: BRAND.blue + '60' }} thumbColor={settings.compactView ? BRAND.blue : BRAND.sub} />} last />
      </SettingsGroup>
      <SettingsGroup title="REGIONAL">
        <SettingsRow icon="currency-gbp" iconBg={BRAND.success + '15'} label="Currency" sub={settings.currency} onPress={() => Alert.alert('Currency', 'Currency selection: GBP, USD, EUR, AED')} />
        <SettingsRow icon="calendar" iconBg={BRAND.blue + '15'} label="Date Format" sub={settings.dateFormat} onPress={() => Alert.alert('Date Format', 'Select date format')} />
        <SettingsRow icon="earth" iconBg={BRAND.mint + '15'} label="Timezone" sub={settings.timezone} onPress={() => Alert.alert('Timezone', 'Select timezone')} />
        <SettingsRow icon="translate" iconBg={BRAND.purple + '15'} label="Language" sub={settings.language} onPress={() => {}} last />
      </SettingsGroup>
      <SettingsGroup title="SYNC & PERFORMANCE">
        <SettingsRow icon="sync" iconBg={BRAND.mint + '15'} label="Auto Sync" sub="Automatically refresh data" right={<Switch value={settings.autoSync} onValueChange={v => saveSetting('autoSync', v)} trackColor={{ false: BRAND.line, true: BRAND.mint + '60' }} thumbColor={settings.autoSync ? BRAND.mint : BRAND.sub} />} />
        <SettingsRow icon="timer" iconBg={BRAND.blue + '15'} label="Sync Interval" sub={`Every ${settings.syncInterval} minutes`} onPress={() => Alert.alert('Sync Interval', 'Set sync frequency')} />
        <SettingsRow icon="cached" iconBg={BRAND.warning + '15'} label="Enable Cache" sub="Store data locally for faster loads" right={<Switch value={settings.cacheEnabled} onValueChange={v => saveSetting('cacheEnabled', v)} trackColor={{ false: BRAND.line, true: BRAND.warning + '60' }} thumbColor={settings.cacheEnabled ? BRAND.warning : BRAND.sub} />} />
        <SettingsRow icon="wifi-off" iconBg={BRAND.danger + '15'} label="Offline Mode" sub="Use cached data without internet" right={<Switch value={settings.offlineMode} onValueChange={v => saveSetting('offlineMode', v)} trackColor={{ false: BRAND.line, true: BRAND.danger + '60' }} thumbColor={settings.offlineMode ? BRAND.danger : BRAND.sub} />} last />
      </SettingsGroup>
      <SettingsGroup title="ADVANCED">
        <SettingsRow icon="flask" iconBg={BRAND.purple + '15'} label="Beta Features" sub="Try experimental features" right={<Switch value={settings.betaFeatures} onValueChange={v => saveSetting('betaFeatures', v)} trackColor={{ false: BRAND.line, true: BRAND.purple + '60' }} thumbColor={settings.betaFeatures ? BRAND.purple : BRAND.sub} />} />
        <SettingsRow icon="school" iconBg={BRAND.mint + '15'} label="Show Tutorials" sub="Display help tips and onboarding" right={<Switch value={settings.showTutorials} onValueChange={v => saveSetting('showTutorials', v)} trackColor={{ false: BRAND.line, true: BRAND.mint + '60' }} thumbColor={settings.showTutorials ? BRAND.mint : BRAND.sub} />} last />
      </SettingsGroup>
    </ScrollView>
  );

  const renderProfile = () => (
    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 16, paddingBottom: 80 }}>
      {/* Avatar + name */}
      <LinearGradient colors={[BRAND.blue, '#163b6c']} style={sts.profileCard}>
        <View style={sts.profileAvatar}><Text style={sts.profileAvatarText}>{profileForm.name.split(' ').map(n => n[0]).join('')}</Text></View>
        <Text style={sts.profileName}>{profileForm.name}</Text>
        <Text style={sts.profileRole}>{profileForm.role} · {profileForm.company}</Text>
        <Text style={sts.profileEmail}>{profileForm.email}</Text>
        <TouchableOpacity style={sts.editProfileBtn} onPress={() => setShowProfileEdit(true)}>
          <MaterialCommunityIcons name="pencil" size={14} color={BRAND.blue} />
          <Text style={{ color: BRAND.blue, fontWeight: '700', fontSize: 13 }}>Edit Profile</Text>
        </TouchableOpacity>
      </LinearGradient>

      <SettingsGroup title="ACCOUNT">
        {[
          { icon: 'account', bg: BRAND.blue + '15', l: 'Full Name', v: profileForm.name },
          { icon: 'email', bg: BRAND.success + '15', l: 'Email', v: profileForm.email },
          { icon: 'phone', bg: BRAND.mint + '15', l: 'Phone', v: profileForm.phone },
          { icon: 'badge-account', bg: BRAND.purple + '15', l: 'Role', v: profileForm.role },
          { icon: 'domain', bg: BRAND.warning + '15', l: 'Company', v: profileForm.company },
          { icon: 'account-group', bg: BRAND.info + '15', l: 'Department', v: profileForm.department },
        ].map((f, i) => (
          <View key={i} style={[sts.settingsRow, i < 5 && { borderBottomWidth: 1, borderBottomColor: BRAND.line }]}>
            <View style={[sts.settingsIcon, { backgroundColor: f.bg }]}><MaterialCommunityIcons name={f.icon} size={16} color={BRAND.sub} /></View>
            <View style={{ flex: 1 }}>
              <Text style={[sts.settingsSub, { color: BRAND.sub }]}>{f.l}</Text>
              <Text style={[sts.settingsLabel, { color: BRAND.text }]}>{f.v}</Text>
            </View>
          </View>
        ))}
      </SettingsGroup>

      <SettingsGroup title="SUBSCRIPTION">
        <View style={[sts.subscriptionCard, { backgroundColor: BRAND.canary + '15', borderColor: BRAND.canary + '40' }]}>
          <MaterialCommunityIcons name="star" size={24} color={BRAND.canary} />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={{ fontSize: 15, fontWeight: '800', color: BRAND.text }}>Enterprise Plan</Text>
            <Text style={{ fontSize: 12, color: BRAND.sub, marginTop: 2 }}>All modules · Unlimited users · Priority support</Text>
            <Text style={{ fontSize: 11, color: BRAND.success, marginTop: 4, fontWeight: '600' }}>Renews 1 Jan 2026 · £4,999/yr</Text>
          </View>
        </View>
      </SettingsGroup>
    </ScrollView>
  );

  const renderNotifications = () => (
    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 16, paddingBottom: 80 }}>
      <SettingsGroup title="MODULE ALERTS">
        {notifications.map((n, i) => (
          <View key={n.key} style={[sts.settingsRow, i < notifications.length - 1 && { borderBottomWidth: 1, borderBottomColor: BRAND.line }]}>
            <View style={[sts.settingsIcon, { backgroundColor: n.color + '18' }]}><MaterialCommunityIcons name={n.icon} size={16} color={n.color} /></View>
            <View style={{ flex: 1 }}>
              <Text style={[sts.settingsLabel, { color: BRAND.text }]}>{n.label}</Text>
              <Text style={[sts.settingsSub, { color: BRAND.sub }]}>{n.sub}</Text>
            </View>
            <Switch value={n.value} onValueChange={() => toggleNotification(n.key)} trackColor={{ false: BRAND.line, true: n.color + '60' }} thumbColor={n.value ? n.color : BRAND.sub} style={{ transform: [{ scaleX: 0.85 }, { scaleY: 0.85 }] }} />
          </View>
        ))}
      </SettingsGroup>
      <SettingsGroup title="QUIET HOURS">
        <SettingsRow icon="sleep" iconBg={BRAND.purple + '15'} label="Quiet Hours" sub="No alerts 22:00 – 07:00" right={<Switch value={true} onValueChange={() => {}} trackColor={{ false: BRAND.line, true: BRAND.purple + '60' }} thumbColor={BRAND.purple} />} />
        <SettingsRow icon="bell-ring" iconBg={BRAND.blue + '15'} label="Critical Alerts Only" sub="Overdue, low stock, failed payments" right={<Switch value={false} onValueChange={() => {}} trackColor={{ false: BRAND.line, true: BRAND.blue + '60' }} thumbColor={BRAND.sub} />} last />
      </SettingsGroup>
    </ScrollView>
  );

  const renderIntegrations = () => (
    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 16, paddingBottom: 80 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Text style={[sts.groupLabel, { color: BRAND.text, textTransform: 'none', fontSize: 15, fontWeight: '700', letterSpacing: 0 }]}>Connected Apps ({integrations.filter(i => i.connected).length})</Text>
      </View>
      {integrations.map((intg, i) => (
        <TouchableOpacity key={intg.id} style={[sts.intgCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line, borderLeftColor: intg.connected ? intg.color : BRAND.line, borderLeftWidth: 4 }]} onPress={() => { setSelectedIntegration(intg); setShowIntegrationDetail(true); }} activeOpacity={0.75}>
          <View style={[sts.intgIcon, { backgroundColor: intg.color + '18' }]}><MaterialCommunityIcons name={intg.icon} size={22} color={intg.color} /></View>
          <View style={{ flex: 1 }}>
            <Text style={[sts.intgName, { color: BRAND.text }]}>{intg.name}</Text>
            <Text style={[sts.intgDesc, { color: BRAND.sub }]}>{intg.desc}</Text>
            {intg.connected && intg.lastSync && <Text style={[sts.intgSync, { color: BRAND.success }]}>Synced {intg.lastSync}</Text>}
          </View>
          <View style={{ alignItems: 'flex-end', gap: 8 }}>
            <View style={[sts.connStatus, { backgroundColor: (intg.connected ? BRAND.success : BRAND.line) + '20' }]}>
              <View style={[sts.connDot, { backgroundColor: intg.connected ? BRAND.success : BRAND.line }]} />
              <Text style={[sts.connText, { color: intg.connected ? BRAND.success : BRAND.sub }]}>{intg.connected ? 'Connected' : 'Disconnected'}</Text>
            </View>
            <Switch value={intg.connected} onValueChange={() => toggleIntegration(intg.id)} trackColor={{ false: BRAND.line, true: intg.color + '60' }} thumbColor={intg.connected ? intg.color : BRAND.sub} style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }] }} />
          </View>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderSecurity = () => (
    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 16, paddingBottom: 80 }}>
      <SettingsGroup title="AUTHENTICATION">
        <SettingsRow icon="fingerprint" iconBg={BRAND.success + '15'} label="Biometric Login" sub="Face ID / Touch ID" right={<Switch value={settings.biometric} onValueChange={v => saveSetting('biometric', v)} trackColor={{ false: BRAND.line, true: BRAND.success + '60' }} thumbColor={settings.biometric ? BRAND.success : BRAND.sub} />} />
        <SettingsRow icon="two-factor-authentication" iconBg={BRAND.blue + '15'} label="Two-Factor Authentication" sub="TOTP app required on login" right={<Switch value={settings.twoFactor} onValueChange={v => saveSetting('twoFactor', v)} trackColor={{ false: BRAND.line, true: BRAND.blue + '60' }} thumbColor={settings.twoFactor ? BRAND.blue : BRAND.sub} />} />
        <SettingsRow icon="lock-reset" iconBg={BRAND.warning + '15'} label="Change Password" sub="Last changed 30 days ago" onPress={() => Alert.alert('Change Password', 'Password change email sent.')} last />
      </SettingsGroup>

      <SettingsGroup title="SESSIONS">
        <SettingsRow icon="devices" iconBg={BRAND.purple + '15'} label="Active Sessions" sub="2 devices signed in" onPress={() => Alert.alert('Sessions', 'iPhone 15 Pro\nMacBook Pro')} />
        <SettingsRow icon="logout" iconBg={BRAND.danger + '15'} label="Sign Out All Devices" sub="Force logout everywhere" onPress={() => Alert.alert('Sign Out', 'Sign out all devices?', [{ text: 'Cancel', style: 'cancel' }, { text: 'Sign Out All', style: 'destructive' }])} last />
      </SettingsGroup>

      <Text style={[sts.groupLabel, { color: BRAND.sub, marginBottom: 10 }]}>RECENT ACTIVITY</Text>
      <View style={[sts.groupCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
        {SECURITY_LOG.map((log, i) => (
          <View key={i} style={[sts.logRow, i < SECURITY_LOG.length - 1 && { borderBottomWidth: 1, borderBottomColor: BRAND.line }]}>
            <View style={[sts.logDot, { backgroundColor: log.success ? BRAND.success : BRAND.danger }]} />
            <View style={{ flex: 1 }}>
              <Text style={[sts.logAction, { color: BRAND.text }]}>{log.action}</Text>
              <Text style={[sts.logDevice, { color: BRAND.sub }]}>{log.device} · {log.location}</Text>
            </View>
            <Text style={[sts.logTime, { color: BRAND.sub }]}>{log.time}</Text>
          </View>
        ))}
      </View>
    </ScrollView>
  );

  const renderData = () => (
    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 16, paddingBottom: 80 }}>
      <SettingsGroup title="STORAGE">
        {[
          { label: 'ERP Cache', size: '12.4 MB', color: BRAND.blue },
          { label: 'Reports', size: '48.2 MB', color: BRAND.success },
          { label: 'Attachments', size: '126 MB', color: BRAND.purple },
          { label: 'Offline Data', size: '8.1 MB', color: BRAND.warning },
        ].map((s, i) => (
          <View key={i} style={[sts.storageRow, i < 3 && { borderBottomWidth: 1, borderBottomColor: BRAND.line }]}>
            <View style={[sts.storageDot, { backgroundColor: s.color }]} />
            <Text style={[sts.storageLabel, { color: BRAND.text, flex: 1 }]}>{s.label}</Text>
            <Text style={[sts.storageSize, { color: s.color }]}>{s.size}</Text>
          </View>
        ))}
      </SettingsGroup>
      <SettingsGroup title="MANAGEMENT">
        <SettingsRow icon="broom" iconBg={BRAND.warning + '15'} label="Clear Cache" sub="Free up local storage" onPress={handleClearCache} />
        <SettingsRow icon="database-export" iconBg={BRAND.success + '15'} label="Export My Data" sub="Download all your ERP data" onPress={handleExportData} />
        <SettingsRow icon="database-remove" iconBg={BRAND.danger + '15'} label="Delete All Data" sub="Permanently remove all local data" onPress={() => Alert.alert('Delete Data', 'This cannot be undone. Are you sure?', [{ text: 'Cancel', style: 'cancel' }, { text: 'Delete', style: 'destructive' }])} last />
      </SettingsGroup>
      <SettingsGroup title="RETENTION">
        <SettingsRow icon="history" iconBg={BRAND.blue + '15'} label="Data Retention Period" sub={`Keep data for ${settings.dataRetention} days`} onPress={() => Alert.alert('Retention', 'Set data retention period')} last />
      </SettingsGroup>
    </ScrollView>
  );

  const renderAbout = () => (
    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 16, paddingBottom: 80 }}>
      <LinearGradient colors={[BRAND.blue, '#163b6c']} style={sts.aboutHeader}>
        <View style={sts.aboutLogo}><MaterialCommunityIcons name="domain" size={40} color="#fff" /></View>
        <Text style={sts.aboutName}>JobEng ERP</Text>
        <Text style={sts.aboutVersion}>Version {APP_VERSION} (Build {BUILD})</Text>
        <Text style={sts.aboutTagline}>Comprehensive Business Management Platform</Text>
      </LinearGradient>
      <SettingsGroup title="APP INFO">
        {[
          { l: 'Version', v: APP_VERSION, i: 'tag' },
          { l: 'Build', v: BUILD, i: 'hammer' },
          { l: 'Platform', v: Platform.OS === 'ios' ? 'iOS' : 'Android', i: 'cellphone' },
          { l: 'Environment', v: 'Production', i: 'server' },
          { l: 'Last Updated', v: '25 Jan 2025', i: 'update' },
        ].map((f, i) => (
          <View key={i} style={[sts.settingsRow, i < 4 && { borderBottomWidth: 1, borderBottomColor: BRAND.line }]}>
            <View style={[sts.settingsIcon, { backgroundColor: BRAND.blue + '15' }]}><MaterialCommunityIcons name={f.i} size={15} color={BRAND.blue} /></View>
            <Text style={[sts.settingsLabel, { color: BRAND.text, flex: 1 }]}>{f.l}</Text>
            <Text style={[sts.settingsSub, { color: BRAND.sub }]}>{f.v}</Text>
          </View>
        ))}
      </SettingsGroup>
      <SettingsGroup title="MODULES">
        {[
          { name: 'CRM & Sales', v: '3.2.1', color: '#f97316' },
          { name: 'Finance & Accounting', v: '4.0.0', color: BRAND.success },
          { name: 'Human Resources', v: '2.8.3', color: BRAND.purple },
          { name: 'Inventory', v: '3.1.0', color: BRAND.mint },
          { name: 'Analytics & BI', v: '3.5.0', color: BRAND.blue },
          { name: 'Projects', v: '2.4.2', color: '#6366f1' },
          { name: 'Payroll', v: '2.1.0', color: BRAND.pink },
          { name: 'Procurement', v: '1.9.4', color: BRAND.warning },
          { name: 'Production', v: '1.6.0', color: BRAND.danger },
        ].map((m, i) => (
          <View key={i} style={[sts.settingsRow, i < 8 && { borderBottomWidth: 1, borderBottomColor: BRAND.line }]}>
            <View style={[sts.storageDot, { backgroundColor: m.color }]} />
            <Text style={[sts.settingsLabel, { color: BRAND.text, flex: 1 }]}>{m.name}</Text>
            <Text style={[sts.settingsSub, { color: BRAND.sub }]}>v{m.v}</Text>
          </View>
        ))}
      </SettingsGroup>
      <SettingsGroup title="LEGAL">
        <SettingsRow icon="file-document" iconBg={BRAND.sub + '15'} label="Terms of Service" onPress={() => {}} />
        <SettingsRow icon="shield-check" iconBg={BRAND.sub + '15'} label="Privacy Policy" onPress={() => {}} />
        <SettingsRow icon="license" iconBg={BRAND.sub + '15'} label="Open Source Licences" onPress={() => {}} last />
      </SettingsGroup>
    </ScrollView>
  );

  const renderContent = () => {
    if (activeTab === 'general') return renderGeneral();
    if (activeTab === 'profile') return renderProfile();
    if (activeTab === 'notifications') return renderNotifications();
    if (activeTab === 'integrations') return renderIntegrations();
    if (activeTab === 'security') return renderSecurity();
    if (activeTab === 'data') return renderData();
    if (activeTab === 'about') return renderAbout();
  };

  return (
    <View style={{ flex: 1, backgroundColor: BRAND.bg }}>
      <StatusBar barStyle="light-content" backgroundColor={BRAND.blue} />
      <LinearGradient colors={[BRAND.blue, '#163b6c']} style={sts.header}>
        <View style={sts.headerRow}>
          <TouchableOpacity style={sts.backBtn} onPress={() => navigation?.goBack()}><MaterialCommunityIcons name="arrow-left" size={22} color="#fff" /></TouchableOpacity>
          <View style={[sts.headerIcon, { backgroundColor: 'rgba(255,255,255,0.2)' }]}><MaterialCommunityIcons name="cog" size={20} color="#fff" /></View>
          <View style={{ flex: 1 }}>
            <Text style={sts.headerTitle}>ERP Settings</Text>
            <Text style={sts.headerSub}>v{APP_VERSION} · {profileForm.role}</Text>
          </View>
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 8, marginBottom: -6 }}>
          {TABS.map(tab => (
            <TouchableOpacity key={tab.id} style={[sts.tab, activeTab === tab.id && sts.tabActive]} onPress={() => setActiveTab(tab.id)}>
              <MaterialCommunityIcons name={tab.icon} size={13} color={activeTab === tab.id ? BRAND.blue : 'rgba(255,255,255,0.75)'} />
              <Text style={[sts.tabText, activeTab === tab.id && { color: BRAND.blue }]}>{tab.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </LinearGradient>

      <Animated.View style={{ flex: 1, opacity: fadeAnim }}>{renderContent()}</Animated.View>

      {/* PROFILE EDIT MODAL */}
      <Modal visible={showProfileEdit} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowProfileEdit(false)}>
        <View style={{ flex: 1, backgroundColor: BRAND.bg }}>
          <LinearGradient colors={[BRAND.blue, '#163b6c']} style={{ flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12, paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 24) + 16 : 16 }}>
            <TouchableOpacity style={sts.modalClose} onPress={() => setShowProfileEdit(false)}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={{ flex: 1, color: '#fff', fontSize: 16, fontWeight: '700' }}>Edit Profile</Text>
          </LinearGradient>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            {[
              { k: 'name', l: 'Full Name', i: 'account' },
              { k: 'email', l: 'Email Address', i: 'email', kb: 'email-address' },
              { k: 'phone', l: 'Phone Number', i: 'phone', kb: 'phone-pad' },
              { k: 'role', l: 'Role / Job Title', i: 'badge-account' },
              { k: 'company', l: 'Company Name', i: 'domain' },
              { k: 'department', l: 'Department', i: 'account-group' },
            ].map(f => (
              <View key={f.k} style={{ marginBottom: 14 }}>
                <Text style={[sts.fieldLabel, { color: BRAND.text }]}>{f.l}</Text>
                <View style={[sts.fieldBox, { backgroundColor: BRAND.surface, borderColor: profileForm[f.k] ? BRAND.blue : BRAND.line }]}>
                  <MaterialCommunityIcons name={f.i} size={16} color={BRAND.sub} />
                  <TextInput value={profileForm[f.k]} onChangeText={v => setProfileForm(p => ({ ...p, [f.k]: v }))} style={[sts.fieldInput, { color: BRAND.text }]} keyboardType={f.kb || 'default'} />
                </View>
              </View>
            ))}
            <TouchableOpacity style={[sts.saveBtn, { backgroundColor: BRAND.blue, opacity: saving ? 0.7 : 1 }]} onPress={handleSaveProfile} disabled={saving}>
              <MaterialCommunityIcons name="check" size={18} color="#fff" />
              <Text style={sts.saveBtnText}>{saving ? 'Saving…' : 'Save Changes'}</Text>
            </TouchableOpacity>
            <View style={{ height: 40 }} />
          </ScrollView>
        </View>
      </Modal>

      {/* INTEGRATION DETAIL MODAL */}
      <Modal visible={showIntegrationDetail} animationType="fade" transparent onRequestClose={() => setShowIntegrationDetail(false)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }}>
          {selectedIntegration && (
            <View style={[sts.intgDetailCard, { backgroundColor: BRAND.surface }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                <View style={[sts.intgDetailIcon, { backgroundColor: selectedIntegration.color + '18' }]}><MaterialCommunityIcons name={selectedIntegration.icon} size={28} color={selectedIntegration.color} /></View>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 17, fontWeight: '800', color: BRAND.text }}>{selectedIntegration.name}</Text>
                  <Text style={{ fontSize: 12, color: BRAND.sub, marginTop: 2 }}>{selectedIntegration.desc}</Text>
                </View>
                <TouchableOpacity onPress={() => setShowIntegrationDetail(false)}><MaterialCommunityIcons name="close" size={20} color={BRAND.sub} /></TouchableOpacity>
              </View>
              <View style={{ flexDirection: 'row', gap: 12 }}>
                <TouchableOpacity style={[sts.intgActionBtn, { flex: 1, backgroundColor: selectedIntegration.connected ? BRAND.danger + '10' : selectedIntegration.color + '10', borderColor: selectedIntegration.connected ? BRAND.danger + '40' : selectedIntegration.color + '40' }]} onPress={() => { setShowIntegrationDetail(false); toggleIntegration(selectedIntegration.id); }}>
                  <MaterialCommunityIcons name={selectedIntegration.connected ? 'link-off' : 'link'} size={16} color={selectedIntegration.connected ? BRAND.danger : selectedIntegration.color} />
                  <Text style={{ color: selectedIntegration.connected ? BRAND.danger : selectedIntegration.color, fontWeight: '700', fontSize: 13 }}>{selectedIntegration.connected ? 'Disconnect' : 'Connect'}</Text>
                </TouchableOpacity>
                {selectedIntegration.connected && (
                  <TouchableOpacity style={[sts.intgActionBtn, { flex: 1, backgroundColor: BRAND.blue + '10', borderColor: BRAND.blue + '40' }]} onPress={() => { setShowIntegrationDetail(false); Alert.alert('Sync', `Syncing ${selectedIntegration.name}…`); }}>
                    <MaterialCommunityIcons name="sync" size={16} color={BRAND.blue} />
                    <Text style={{ color: BRAND.blue, fontWeight: '700', fontSize: 13 }}>Sync Now</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          )}
        </View>
      </Modal>
    </View>
  );
};

const sts = StyleSheet.create({
  header: { paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 24) : 0, paddingHorizontal: 16, paddingBottom: 0 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12 },
  backBtn: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  headerIcon: { width: 36, height: 36, borderRadius: 11, justifyContent: 'center', alignItems: 'center' },
  headerTitle: { color: '#fff', fontSize: 16, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.75)', fontSize: 11, marginTop: 1 },
  tab: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 18, marginRight: 6, marginBottom: 8 },
  tabActive: { backgroundColor: '#fff' },
  tabText: { color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: '600' },
  groupLabel: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8, marginLeft: 4 },
  groupCard: { borderRadius: 16, borderWidth: 1, overflow: 'hidden', elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  settingsRow: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14 },
  settingsIcon: { width: 34, height: 34, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  settingsLabel: { fontSize: 14, fontWeight: '600' },
  settingsSub: { fontSize: 11, marginTop: 1 },
  profileCard: { borderRadius: 20, padding: 24, alignItems: 'center', marginBottom: 20 },
  profileAvatar: { width: 72, height: 72, borderRadius: 36, backgroundColor: 'rgba(255,255,255,0.25)', justifyContent: 'center', alignItems: 'center', marginBottom: 14 },
  profileAvatarText: { color: '#fff', fontSize: 24, fontWeight: '900' },
  profileName: { color: '#fff', fontSize: 20, fontWeight: '800', marginBottom: 4 },
  profileRole: { color: 'rgba(255,255,255,0.8)', fontSize: 13, marginBottom: 4 },
  profileEmail: { color: 'rgba(255,255,255,0.7)', fontSize: 12, marginBottom: 16 },
  editProfileBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#fff', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20 },
  subscriptionCard: { flexDirection: 'row', alignItems: 'center', padding: 16, borderRadius: 14, borderWidth: 1 },
  intgCard: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 13, borderRadius: 14, borderWidth: 1, marginBottom: 10, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3 },
  intgIcon: { width: 46, height: 46, borderRadius: 13, justifyContent: 'center', alignItems: 'center' },
  intgName: { fontSize: 13, fontWeight: '700', marginBottom: 2 },
  intgDesc: { fontSize: 11 },
  intgSync: { fontSize: 10, fontWeight: '600', marginTop: 2 },
  connStatus: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  connDot: { width: 6, height: 6, borderRadius: 3 },
  connText: { fontSize: 10, fontWeight: '700' },
  logRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 13 },
  logDot: { width: 8, height: 8, borderRadius: 4 },
  logAction: { fontSize: 13, fontWeight: '600', marginBottom: 2 },
  logDevice: { fontSize: 11 },
  logTime: { fontSize: 10 },
  storageRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 14 },
  storageDot: { width: 10, height: 10, borderRadius: 5 },
  storageLabel: { fontSize: 13, fontWeight: '600' },
  storageSize: { fontSize: 13, fontWeight: '700' },
  aboutHeader: { borderRadius: 20, padding: 28, alignItems: 'center', marginBottom: 20 },
  aboutLogo: { width: 72, height: 72, borderRadius: 22, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center', marginBottom: 14 },
  aboutName: { color: '#fff', fontSize: 22, fontWeight: '900', marginBottom: 4 },
  aboutVersion: { color: 'rgba(255,255,255,0.75)', fontSize: 13, marginBottom: 6 },
  aboutTagline: { color: 'rgba(255,255,255,0.6)', fontSize: 11, textAlign: 'center' },
  modalClose: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  fieldLabel: { fontSize: 13, fontWeight: '600', marginBottom: 6 },
  fieldBox: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, borderWidth: 1 },
  fieldInput: { flex: 1, fontSize: 14 },
  saveBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 16, borderRadius: 16 },
  saveBtnText: { color: '#fff', fontSize: 16, fontWeight: '700' },
  intgDetailCard: { borderRadius: 24, padding: 24, margin: 16, elevation: 10, shadowColor: '#000', shadowOffset: { width: 0, height: -4 }, shadowOpacity: 0.15, shadowRadius: 12 },
  intgDetailIcon: { width: 56, height: 56, borderRadius: 16, justifyContent: 'center', alignItems: 'center' },
  intgActionBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 14, borderRadius: 14, borderWidth: 1 },
});

export default ERPSettingsScreen;